import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Emblem } from '../../components/Emblem';
import { Stripe } from '../../components/Stripe';
import { validateExternalOrder } from '../../api/externalOrders';
import { useShop } from '../ShopState';

export function Gate() {
  const navigate = useNavigate();
  const { setVerification } = useShop();
  const [firstName, setFirstName] = useState('');
  const [externalOrderId, setExternalOrderId] = useState('');
  const [state, setState] = useState<'idle' | 'notfound' | 'failed' | 'loading'>('idle');
  const [exhausted, setExhausted] = useState<{ allowance: number } | null>(null);
  const [shippingClosed, setShippingClosed] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('loading');
    setExhausted(null);
    setShippingClosed(false);
    // A thrown request is not a verdict on the order. Reporting both as "not found" sends the
    // holder chasing a typo while the real fault is server-side.
    try {
      const res = await validateExternalOrder(firstName, externalOrderId);
      if (res.valid) {
        // Out of shirts is a valid order, not an unknown one, and it gets its own message. The
        // holder is not sent to any existing order: the gate deliberately never learns which
        // orders exist, so recovery goes through the emailed link or the lookup form.
        if (res.remaining <= 0) {
          setExhausted({ allowance: res.allowance });
          setState('idle');
          return;
        }
        // Every earlier parcel has already gone out, so there is nothing left to add shirts to.
        // Stopping here rather than at checkout spares the holder filling a cart the shop will
        // refuse, and refusing at all is deliberate: the alternative is charging for a second
        // parcel they were told they would not pay for.
        if (res.shippingMode === 'closed') {
          setShippingClosed(true);
          setState('idle');
          return;
        }
        // The typed first name is kept deliberately: holders may be seeded ASCII-folded, and this
        // name goes on the parcel label. The id is taken from the API in its stored form.
        setVerification({
          firstName,
          externalOrderId: res.externalOrderId,
          allowance: res.allowance,
          alreadyOrdered: res.alreadyOrdered,
          remaining: res.remaining,
          byColor: res.byColor,
          shippingMode: res.shippingMode,
        });
        navigate('/sklep');
        return;
      }
      setState('notfound');
    } catch {
      setState('failed');
    }
  };

  return (
    <div className="phone-screen">
      <div
        style={{
          position: 'relative',
          zIndex: 5,
          padding: '12px 18px 0',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'var(--shop-fg)',
        }}
        className="mono"
      >
        <span>9:41</span>
        <span>●●●●● ⌬</span>
      </div>
      <Stripe>OPERACJA TENERYFA · INICJATYWA FANOWSKA</Stripe>

      <form
        onSubmit={submit}
        className="scroll"
        style={{ minHeight: 'calc(100% - 30px)', position: 'relative', zIndex: 5, padding: '24px 22px 24px' }}
      >
        <fieldset disabled={state === 'loading'} style={{ border: 0, padding: 0, margin: 0, display: 'contents' }}>
        <div style={{ textAlign: 'center' }}>
          <Emblem size={96} style={{ margin: '0 auto' }} />
          <div
            className="mono"
            style={{ fontSize: 10, letterSpacing: '.2em', color: 'var(--orange)', marginTop: 10 }}
          >
            KONCERT · 28.11.2026 · TENERYFA
          </div>
        </div>

        <div className="display" style={{ fontSize: 30, marginTop: 26, lineHeight: 0.95, whiteSpace: 'nowrap' }}>
          MASZ BILET<span style={{ color: 'var(--orange)' }}>?</span>
        </div>
        <div
          style={{
            marginTop: 10,
            fontSize: 13,
            color: 'rgba(var(--shop-muted-rgb),.7)',
            lineHeight: 1.5,
          }}
        >
          Koszulki dostępne tylko dla osób z biletem na koncert. Wpisz numer zamówienia z
          potwierdzenia zakupu biletów i imię{' '}
          <b style={{ color: 'var(--shop-fg)' }}>dokładnie</b> jak przy zakupie - sprawdzamy w bazie.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
          <div className="field">
            <label htmlFor="gate-firstname">Imię (jak przy zakupie biletów)</label>
            <input
              id="gate-firstname"
              className="input"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="gate-order">Numer zamówienia</label>
            <input
              id="gate-order"
              className="input mono"
              required
              value={externalOrderId}
              onChange={(e) => setExternalOrderId(e.target.value)}
              style={{ letterSpacing: '.08em' }}
            />
            <div
              className="mono"
              style={{
                marginTop: 6,
                fontSize: 10,
                color: 'rgba(var(--shop-muted-rgb),.5)',
                letterSpacing: '.08em',
              }}
            >
              ZNAJDZIESZ GO W POTWIERDZENIU ZAKUPU BILETÓW
            </div>
          </div>
        </div>

        {exhausted && (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              border: '2px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
            }}
          >
            <div className="mono" style={{ fontSize: 11, color: 'var(--orange)', letterSpacing: '.14em' }}>
              ✕ LIMIT WYKORZYSTANY
            </div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Na to zamówienie kupiono już {exhausted.allowance} z {exhausted.allowance} koszulek.{' '}
              <Link to="/zamowienie" className="mono" style={{ color: 'var(--orange)' }}>
                ZNAJDŹ SWOJE ZAMÓWIENIE →
              </Link>
            </div>
          </div>
        )}

        {shippingClosed && (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              border: '2px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
            }}
          >
            <div className="mono" style={{ fontSize: 11, color: 'var(--orange)', letterSpacing: '.14em' }}>
              ✕ PACZKA JUŻ WYSŁANA
            </div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Twoje koszulki z tego zamówienia są już w drodze, więc nie dołożymy do nich kolejnych.
              Napisz na{' '}
              <a href="mailto:kontakt@operacjateneryfa.pl" style={{ color: 'var(--orange)' }}>
                kontakt@operacjateneryfa.pl
              </a>
              , jeśli chcesz domówić.
            </div>
          </div>
        )}

        {state === 'notfound' && (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              border: '2px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
            }}
          >
            <div className="mono" style={{ fontSize: 11, color: 'var(--orange)', letterSpacing: '.14em' }}>
              ✕ NIE ZNALEZIONO
            </div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Nie ma takiej kombinacji imię + numer zamówienia w bazie. Sprawdź pisownię (polskie znaki!) lub napisz do
              nas:{' '}
              <a href="mailto:kontakt@operacjateneryfa.pl" className="mono" style={{ color: 'var(--orange)' }}>
                kontakt@operacjateneryfa.pl
              </a>
            </div>
          </div>
        )}
        {state === 'failed' && (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              border: '2px solid var(--orange)',
              background: 'rgba(255,107,26,.08)',
            }}
          >
            <div className="mono" style={{ fontSize: 11, color: 'var(--orange)', letterSpacing: '.14em' }}>
              ✕ BŁĄD POŁĄCZENIA
            </div>
            <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              Nie udało się sprawdzić zamówienia - to nasza wina, nie Twoich danych. Spróbuj za chwilę.
            </div>
          </div>
        )}
        {state === 'idle' && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              border: '1px dashed rgba(var(--shop-muted-rgb),.25)',
              fontSize: 11,
              color: 'rgba(var(--shop-muted-rgb),.55)',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
            className="mono"
          >
            WPISZ DANE → SPRAWDZIMY W BAZIE ZAMÓWIEŃ
          </div>
        )}

        <button
          type="submit"
          className="btn full"
          style={{ marginTop: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
          disabled={state === 'loading'}
          aria-busy={state === 'loading'}
        >
          {state === 'loading' && <span className="spinner" aria-hidden="true" />}
          {state === 'loading' ? 'SPRAWDZAM…' : 'DO ZAMÓWIENIA →'}
        </button>

        <div
          style={{
            marginTop: 16,
            textAlign: 'center',
            fontSize: 11,
            color: 'rgba(var(--shop-muted-rgb),.5)',
            lineHeight: 1.5,
          }}
        >
          Nie masz biletu?{' '}
          <a
            href="http://fast-tickets.com/events/43"
            target="_blank"
            rel="noreferrer"
            className="mono"
            style={{ color: 'var(--orange)', textDecoration: 'none' }}
          >
            ↗ KUP BILET
          </a>
          <div style={{ marginTop: 10 }}>
            <Link
              to="/zamowienie"
              className="mono"
              style={{ color: 'var(--orange)', textDecoration: 'none' }}
            >
              MASZ JUŻ ZAMÓWIENIE? ZNAJDŹ JE →
            </Link>
          </div>
        </div>
        </fieldset>
      </form>
    </div>
  );
}
